import { Box } from "@mantine/core";

import { Text } from "@/components/ui/elements";

import type { TrustScoreGrade } from "@/gql/__generated__/graphql";

/** ランクに応じた金属色（バッジの背景・文字色・枠線・影） */
const TRUST_SCORE_STYLES: Record<
  TrustScoreGrade,
  { bg: string; color: string; border: string; shadow: string }
> = {
  // プラチナ: 銀白
  A: {
    bg: "linear-gradient(140deg, #fbfcfd 0%, #dfe5ea 45%, #f2f5f7 100%)",
    color: "#48525e",
    border: "#cdd5dc",
    shadow: "rgba(100, 116, 139, 0.25)",
  },
  // ゴールド: 金
  B: {
    bg: "linear-gradient(140deg, #fdf3cf 0%, #e3bf52 45%, #f6e4a4 100%)",
    color: "#6b5411",
    border: "#cfa93e",
    shadow: "rgba(161, 123, 22, 0.25)",
  },
  // シルバー: 灰
  C: {
    bg: "linear-gradient(140deg, #f1f2f3 0%, #bdbfc3 45%, #dedfe2 100%)",
    color: "#4f5257",
    border: "#b4b6ba",
    shadow: "rgba(100, 103, 110, 0.25)",
  },
  // ブロンズ: 銅
  D: {
    bg: "linear-gradient(140deg, #f0cfb2 0%, #bd8356 45%, #ddae84 100%)",
    color: "#5c3a1e",
    border: "#b4825a",
    shadow: "rgba(133, 86, 48, 0.25)",
  },
};

/** 評価対象の実績なし（ランクを付けない）ときの表示名。マイページとキャンセル確認画面で共通にする */
export const NO_EVALUATION_RECORD_DISPLAY_NAME = "評価対象の実績なし";

/** 評価対象の実績なし（ランクを付けない）ときの配色。ランクと誤解されないよう金属色を使わない */
const NO_EVALUATION_RECORD_STYLE = {
  bg: "var(--mantine-color-gray-0)",
  color: "var(--mantine-color-gray-7)",
  border: "var(--mantine-color-gray-3)",
  shadow: "rgba(0, 0, 0, 0.06)",
};

/** サイズ別の寸法。md はマイページのカード、sm はキャンセル確認画面で使う */
const SIZES = {
  md: { minWidth: 92, padding: "9px 12px", fz: 15 },
  sm: { minWidth: 72, padding: "3px 10px", fz: 13 },
} as const;

type Props = {
  displayName: string;
  size?: keyof typeof SIZES;
} & (
  | { grade: TrustScoreGrade; noEvaluationRecord?: false }
  /** 評価対象の実績なし。ランクを持たないため中立色で表示する */
  | { grade?: never; noEvaluationRecord: true }
);

/** ランクの表示名を金属色のバッジで表示する */
export function TrustScoreBadge({
  grade,
  noEvaluationRecord,
  displayName,
  size = "md",
}: Props) {
  // ランクが増えたときは Record の定義でビルドが落ちる。配信済みのバンドル向けの保険として既定値を置く
  const styles = noEvaluationRecord
    ? NO_EVALUATION_RECORD_STYLE
    : (TRUST_SCORE_STYLES[grade] ?? TRUST_SCORE_STYLES.C);
  const { minWidth, padding, fz } = SIZES[size];

  return (
    <Box
      style={{
        minWidth,
        padding,
        borderRadius: "var(--mantine-radius-md)",
        background: styles.bg,
        border: `1px solid ${styles.border}`,
        boxShadow: `inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 1px 3px ${styles.shadow}`,
        textAlign: "center",
      }}
    >
      <Text
        fz={fz}
        fw={700}
        lh={1.3}
        c={styles.color}
        style={{ letterSpacing: "0.04em" }}
      >
        {displayName}
      </Text>
    </Box>
  );
}
